import { logoutResponseSchema, meResponseSchema, type MeResponse } from '@complan/contracts';
import type { Capability } from '@complan/domain';
import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import { ApiRequestError } from '@/shared/api/api-error';
import { apiFetch } from '@/shared/api/client';
import { queryKeys } from '@/shared/query/query-keys';

/**
 * Session bootstrap against `GET /api/me` (design.md, "Identity and session").
 *
 * Roles and capabilities are resolved by the server per request, so the client
 * never derives them from a token; it renders controls from `capabilities` and the
 * server independently checks the same capability before any handler runs. The
 * zero-role principal (R1.3) is a first-class value here: empty `roles`, empty
 * `capabilities`, and `accessRequestPath` naming where to ask for access.
 */
export async function fetchMe(): Promise<MeResponse> {
  const body = await apiFetch<unknown>('/api/me');
  return meResponseSchema.parse(body);
}

export function useSession(): UseQueryResult<MeResponse, unknown> {
  return useQuery({
    queryKey: queryKeys.me(),
    queryFn: fetchMe,
    /**
     * A 401 or 403 is a settled verdict, not a transient: `AppShell` acts on it
     * immediately by starting the OIDC exchange, and retrying would only delay
     * that behind a backoff.
     *
     * Everything else *is* transient and must not be treated as fatal. This
     * previously read `retry: false`, justified by the 401 case alone - but that
     * also made a dropped socket, a proxy timeout or a single 5xx terminal for
     * the *whole application*, because the shell renders nothing until the
     * session query settles. That is not a hypothetical: navigating to the
     * Calendar_View fans out into a dozen concurrent reads (the bars payload,
     * the year's Sales_Cycles, seven selectable master-data sets, the assignable
     * owners, the Saved_Views), the browser caps concurrent connections per
     * origin, and every one of those requests re-saves the session row on the
     * way through (`rolling: true`, see `api/src/server/session-plugin.ts`). A
     * `/api/me` refetch landing in the middle of that is the one request whose
     * failure blanks the screen. Retrying it, exactly as `query-provider.tsx`
     * retries every other read, is what makes a blip a blip.
     */
    retry: (failureCount, error) => {
      if (
        error instanceof ApiRequestError &&
        (error.code === 'unauthenticated' || error.code === 'forbidden')
      ) {
        return false;
      }
      return failureCount < 2;
    },
    staleTime: 5 * 60 * 1000,
  });
}

/**
 * Signs the user out (R1.6): `POST /auth/logout` deletes the server-side
 * session row, then the browser goes to the identity provider's end-session
 * URL when it returned one, so the provider's own session ends too and the
 * next sign-in asks for an account instead of silently reusing this one. The
 * provider sends the browser back to the configured post-logout URL.
 *
 * Without an end-session URL (password-login mode, or a provider that does not
 * support RP-initiated logout) the browser reloads the app root, which starts
 * a fresh sign-in.
 */
export async function signOut(): Promise<void> {
  const body = await apiFetch<unknown>('/auth/logout', { method: 'POST' });
  const { endSessionUrl } = logoutResponseSchema.parse(body);
  window.location.assign(endSessionUrl ?? '/');
}

/** Whether a Principal holds a capability; the client's render gate (R2.5). */
export function hasCapability(me: MeResponse | undefined, capability: Capability): boolean {
  return me?.capabilities.includes(capability) ?? false;
}
